import { CreateProject, Org, Orgs, SelectOrg } from "@views/index"
import { NavigationGuardNext, RouteLocationNormalized } from "vue-router"
import ProjectLayout from "@/layouts/ProjectLayout.vue"
import { getOrg, getNumberOfOrgs } from "@/services/clerk"
import { useProjectStore } from "@stores/projectStore"
import {
   composeGuards,
   requireLogin,
   orgGuard,
   subscriptionGuard,
} from "../guards/composable"
import { useOnboardingStore } from "@stores/onboardingStore"

export default [
   {
      path: "/orgs/select",
      name: "SelectOrg",
      component: SelectOrg,
      beforeEnter: async (
         to: RouteLocationNormalized,
         from: RouteLocationNormalized,
         next: NavigationGuardNext,
      ) => {
         // Custom composed guard for org selection
         const selectOrgGuard = composeGuards(requireLogin, async () => {
            // Check if user has no orgs and redirect to create org
            const numOrgs = await getNumberOfOrgs()
            if (numOrgs === 0) {
               return {
                  continue: false,
                  redirectTo: { name: "OnboardingCreateOrg" },
               }
            }

            // Allow users to select org regardless of subscription status
            // They'll be prompted for subscription when accessing protected features
            return { continue: true }
         })

         return selectOrgGuard(to, from, next)
      },
   },
   {
      name: "CreateProject",
      path: "/orgs/:orgSlug/projects/create",
      component: CreateProject,
      meta: {
         layout: "OrgsLayout",
      },
      beforeEnter: subscriptionGuard, // Allow project creation during trials/onboarding
   },
   {
      path: "/orgs/:orgSlug/projects",
      name: "OrgProjects",
      component: ProjectLayout,
      meta: {
         layout: "OrgsLayout",
      },
      beforeEnter: subscriptionGuard, // Allow viewing projects during trials/onboarding
   },
   {
      path: "/orgs/setup",
      name: "OrgsSetup",
      component: Orgs,
      meta: {
         layout: "OrgsLayout",
      },
      beforeEnter: async (
         to: RouteLocationNormalized,
         from: RouteLocationNormalized,
         next: NavigationGuardNext,
      ) => {
         // Get the new org and navigate to it
         const org = await getOrg()
         return next({ name: "OrgHome", params: { orgSlug: org!.slug } })
      },
   },
   {
      path: "/orgs/setup-checkout",
      name: "OrgsSetupCheckout",
      component: () => import("@views/onboarding/SetupCheckout.vue"),
      meta: {
         layout: "AppLayoutDefault",
      },
      beforeEnter: async (
         to: RouteLocationNormalized,
         from: RouteLocationNormalized,
         next: NavigationGuardNext,
      ) => {
         const { tier } = useOnboardingStore()
         if (!tier) {
            return next({ name: "SelectSubscription" })
         }
         return next()
      },
   },
   {
      path: "/orgs/:orgSlug",
      name: "OrgHome",
      component: Org,
      meta: {
         layout: "OrgsLayout",
      },
      beforeEnter: async (
         to: RouteLocationNormalized,
         from: RouteLocationNormalized,
         next: NavigationGuardNext,
      ) => {
         const projectStore = useProjectStore()
         await projectStore.getActiveOrgProjects()
         return orgGuard(to, from, next) // Use standard org guard for consistency
      },
   },
]
